import { installedApps } from "@norn/widget-renderer/model"
import { Link, useParams, useSearch } from "@tanstack/react-router"
import { Check, ChevronDown, Layers, SquareArrowOutUpRight } from "lucide-react"
import { type ReactNode, useEffect, useMemo, useState } from "react"
import { OpenInGraph } from "@/components/graph/open-in-graph"
import { PageHeader } from "@/components/shell/page-header"
import { ErrorState } from "@/components/states"
import { Button } from "@/components/ui/button"
import { Screen } from "@/components/ui/screen"
import { Skeleton } from "@/components/ui/skeleton"
import { Tooltip } from "@/components/ui/tooltip"
import { refusalOfError } from "@/lib/api"
import type { Scope } from "@/lib/connection"
import { useLocale, useT } from "@/lib/i18n"
import { useObject } from "@/lib/queries"
import { useScope } from "@/lib/scope"
import { usePrincipal } from "@/widget/runtime"
import { cn } from "@/lib/utils"
import { journey, useHistorySpace, useJourney, useOntology, useReferenceIndex, useTitleOf } from "./hooks"
import { HowItWorks } from "./how-it-works"
import { LensSwitcher } from "./lens"
import { areaOfType, type FieldMeta, type LifecycleMeta, optionLabel, recordTitle, type TypeMeta } from "./ontology"
import { ProvenanceSection } from "./provenance"
import { RecordCommands } from "./record-commands"
import { LinkedProgress, RelatedPanel } from "./related"
import { FieldValue, HiddenChip, isMasked, type Masked, RefusalNote, RefusalSummary, StatusPill } from "./values"

export function RecordPage() {
  const { objectType, key } = useParams({ from: "/$orgId/$environmentId/records/$objectType/$key" })
  return <RecordView key={`${objectType}/${key}`} objectType={objectType} objectKey={key} />
}

function RecordView({ objectType, objectKey }: { objectType: string; objectKey: string }) {
  const t = useT()
  const scope = useScope()
  const { preference } = useLocale()
  const search = useSearch({ strict: false }) as { via?: string }
  const { ontology, defs } = useOntology(scope)
  const principal = usePrincipal(scope)
  const object = useObject(scope, objectType, objectKey)
  const concrete = object.data?.objectType ?? objectType
  const type = ontology?.types.get(concrete)
  const area = ontology ? areaOfType(ontology, concrete) : undefined
  const values = useMemo(() => (object.data?.values ?? {}) as Record<string, unknown>, [object.data])
  const masked = object.data?.masked as Masked
  const title = recordTitle(type, values, masked as Record<string, unknown> | undefined, preference.locale)
  const displayTitle = title.title ?? (type ? `${type.label} ${objectKey}` : objectKey)
  const space = useHistorySpace(scope)
  const trail = useJourney(space)
  const refTargets = useMemo(() => referenceTargets(type, values, masked), [type, values, masked])
  const refs = useReferenceIndex(scope, refTargets)
  const titleOf = useTitleOf(ontology)
  const refSummary = refTargets
    .map((r) => {
      const t = titleOf(refs(r.objectType, r.key).data, r.key)
      return t.isKey ? null : t.title
    })
    .filter((x): x is string => Boolean(x))

  useEffect(() => {
    if (object.isSuccess && space) journey.open(space, { objectType: concrete, key: objectKey, title: displayTitle }, search.via === "link")
    // Only on arrival and when the readable title changes.
  }, [object.isSuccess, space, concrete, objectKey, displayTitle, search.via])

  const atEnd = trail.length > 1 && trail[trail.length - 1]?.key === objectKey && trail[trail.length - 1]?.objectType === concrete
  const crumbs: ReactNode[] = atEnd
    ? [
        <Link key="home" to="/$orgId/$environmentId" params={scope}>
          {t("ws.nav.home")}
        </Link>,
        ...trail.slice(Math.max(0, trail.length - 4), -1).map((step) => (
          <Link key={`${step.objectType}/${step.key}`} to="/$orgId/$environmentId/records/$objectType/$key" params={{ ...scope, objectType: step.objectType, key: step.key }} search={{ via: "link" }}>
            {step.title}
          </Link>
        )),
        displayTitle,
      ]
    : [
        <Link key="home" to="/$orgId/$environmentId" params={scope}>
          {t("ws.nav.home")}
        </Link>,
        ...(area
          ? [
              <Link key="area" to="/$orgId/$environmentId/areas/$areaId" params={{ ...scope, areaId: area.id }}>
                {area.label}
              </Link>,
            ]
          : []),
        <Link key="type" to="/$orgId/$environmentId/records/$objectType" params={{ ...scope, objectType: concrete }}>
          {type?.pluralLabel ?? concrete}
        </Link>,
        displayTitle,
      ]

  const refused = object.isError ? refusalOfError(object.error) : null
  const identifier = type?.identity.find((id) => values[id] !== undefined && values[id] !== objectKey)
  const appPage = useMemo(() => {
    if (!defs.data || !type) return null
    for (const app of installedApps(defs.data)) {
      const page = app.ux.pages.find((p) => p.contextParams.some((c) => c.required && c.runtimeKeyOf?.id === type.artifactId))
      if (page) return { appId: app.appId, pageId: page.pageId, param: page.contextParams.find((c) => c.required)?.paramId ?? "key" }
    }
    return null
  }, [defs.data, type])

  return (
    <>
      <PageHeader
        crumbs={crumbs}
        actions={
          <>
            <HowItWorks lines={masked && Object.keys(masked).length ? ["ws.how.record", "ws.how.restricted"] : ["ws.how.record"]} />
            <Tooltip content={t("ws.record.inspect")}>
              <Button size="icon-sm" variant="ghost" asChild>
                <Link to="/$orgId/$environmentId/objects/$objectType/$key" params={{ ...scope, objectType: concrete, key: objectKey }} aria-label={t("ws.record.inspect")}>
                  <Layers />
                </Link>
              </Button>
            </Tooltip>
          </>
        }
      />
      <div className="min-h-0 flex-1 overflow-y-auto">
        <Screen width="board" className="space-y-8 px-4 py-8 md:px-8">
          {defs.isError ? (
            <ErrorState error={defs.error} what={t("definitions.what")} onRetry={() => void defs.refetch()} />
          ) : object.isPending || !ontology ? (
            <RecordSkeleton />
          ) : object.isError ? (
            refused && (refused.refusal.category === "visibility" || refused.refusal.category === "permission") ? (
              <div className="max-w-xl space-y-3">
                <h1 className="text-lg font-semibold">{t("ws.record.notVisible")}</h1>
                <RefusalNote error={object.error} title={t("ws.record.notVisibleHelp", { type: type?.label ?? concrete })} />
              </div>
            ) : (
              <ErrorState error={object.error} what={`${type?.label ?? concrete} ${objectKey}`} onRetry={() => void object.refetch()} />
            )
          ) : (
            <>
              <header className="space-y-4">
                <div className="flex flex-wrap items-center gap-x-4 gap-y-2">
                <p className="flex flex-1 items-center gap-1.5 text-xs text-muted-foreground">
                  {area ? (
                    <Link to="/$orgId/$environmentId/areas/$areaId" params={{ ...scope, areaId: area.id }} className="hover:text-foreground">
                      {area.label}
                    </Link>
                  ) : null}
                  {area ? <span aria-hidden>/</span> : null}
                  <Link to="/$orgId/$environmentId/records/$objectType" params={{ ...scope, objectType: concrete }} className="hover:text-foreground">
                    {type?.label ?? concrete}
                  </Link>
                </p>
                <LensSwitcher />
                </div>
                <div className="flex flex-wrap items-start gap-x-4 gap-y-3">
                  <div className="min-w-0 flex-1 space-y-1.5">
                    <h1 className="flex flex-wrap items-center gap-3 text-[1.75rem] leading-tight font-semibold tracking-[-0.02em] text-balance">
                      <span dir="auto">{displayTitle}</span>
                      {title.hidden ? <HiddenChip /> : null}
                      {type?.statusField && typeof values[type.statusField] === "string" && !isMasked(masked, type.statusField) ? (
                        <StatusPill value={values[type.statusField] as string} field={type.fields.find((f) => f.id === type.statusField)} type={type} />
                      ) : null}
                    </h1>
                    <p className="flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-muted-foreground">
                      {title.title ? (
                        <span dir="ltr">
                          {objectKey}
                        </span>
                      ) : null}
                      {identifier ? (
                        <span dir="ltr">
                          {String(values[identifier])}
                        </span>
                      ) : null}
                      {!title.title && refSummary.length ? <span dir="auto">{refSummary.join(" · ")}</span> : null}
                      {title.alternate ? <span dir="auto">{title.alternate}</span> : null}
                      {object.data.observedDeleted ? <span className="text-destructive">{t("objects.deletedInSource")}</span> : null}
                    </p>
                  </div>
                  <RecordActions type={type} values={values} scope={scope} objectKey={objectKey} appPage={appPage} />
                </div>
                {type && defs.data ? (
                  <RecordCommands
                    scope={scope}
                    actor={principal.status === "ready" ? principal.actor : null}
                    type={type}
                    objectKey={objectKey}
                    version={object.data.version}
                    values={values}
                    masked={masked}
                    artifacts={defs.data.artifacts}
                  />
                ) : null}
                <LinkedProgress scope={scope} self={{ objectType: concrete, key: objectKey }} ontology={ontology} />
                {type?.lifecycle && typeof values[type.lifecycle.property] === "string" && !isMasked(masked, type.lifecycle.property) ? (
                  <LifecycleTrack lifecycle={type.lifecycle} current={values[type.lifecycle.property] as string} field={type.fields.find((f) => f.id === type.lifecycle?.property)} />
                ) : null}
              </header>
              {type ? <KeyFacts scope={scope} type={type} values={values} masked={masked} refTargets={refTargets} refs={refs} /> : null}
              <div className="grid gap-10 lg:grid-cols-[minmax(0,1fr)_22rem]">
                <div className="min-w-0 space-y-10">
                  {type ? <Details type={type} values={values} masked={masked} /> : null}
                  <ProvenanceSection scope={scope} type={type} types={ontology.types} objectKey={objectKey} values={values} masked={masked} version={object.data.version} />
                </div>
                <aside className="min-w-0 lg:sticky lg:top-6 lg:self-start">
                  <RelatedPanel scope={scope} self={{ objectType: concrete, key: objectKey }} ontology={ontology} />
                  <div className="mt-4 flex items-center gap-3 px-1 text-xs text-muted-foreground">
                    <OpenInGraph object={{ objectType: concrete, key: objectKey }} />
                  </div>
                </aside>
              </div>
            </>
          )}
        </Screen>
      </div>
    </>
  )
}

function RecordSkeleton() {
  return (
    <div className="space-y-6">
      <Skeleton className="h-3 w-40" />
      <Skeleton className="h-8 w-80" />
      <Skeleton className="h-3 w-56" />
      <div className="grid grid-cols-4 gap-6 pt-4">
        {[0, 1, 2, 3].map((i) => (
          <Skeleton key={i} className="h-12" />
        ))}
      </div>
      <Skeleton className="h-64 w-full rounded-2xl" />
    </div>
  )
}

/** Lifecycle as a quiet track: done states are ticked, the current one is marked, the rest wait. */
function LifecycleTrack({ lifecycle, current, field }: { lifecycle: LifecycleMeta; current: string; field?: FieldMeta }) {
  const t = useT()
  const index = lifecycle.states.indexOf(current)
  const terminalOther = lifecycle.terminal.includes(current) && index !== lifecycle.states.length - 1
  const shown = lifecycle.states.filter((s) => !lifecycle.terminal.includes(s) || s === current || s === lifecycle.states[lifecycle.states.length - 1])
  return (
    <ol aria-label={t("ws.record.lifecycle")} className="flex flex-wrap items-center gap-y-2 text-xs">
      {shown.map((state, i) => {
        const pos = lifecycle.states.indexOf(state)
        const done = !terminalOther && pos < index
        const here = state === current
        return (
          <li key={state} className="flex items-center">
            {i > 0 ? <span aria-hidden className={cn("mx-2 h-px w-6", done || here ? "bg-foreground/40" : "bg-border")} /> : null}
            <span
              aria-current={here ? "step" : undefined}
              className={cn(
                "inline-flex h-6 items-center gap-1.5 rounded-full px-2.5",
                here ? "bg-foreground text-background" : done ? "text-foreground" : "text-subtle-foreground",
              )}
            >
              {done ? <Check className="size-3" aria-hidden /> : null}
              {field?.options.find((o) => o.value === state)?.label ?? optionLabel(state)}
            </span>
          </li>
        )
      })}
    </ol>
  )
}

/** Lifecycle moves the record can make now; others say when they become possible. Execution stays in the governed app. */
function RecordActions({
  type,
  values,
  scope,
  objectKey,
  appPage,
}: {
  type: TypeMeta | undefined
  values: Record<string, unknown>
  scope: Scope
  objectKey: string
  appPage: { appId: string; pageId: string; param: string } | null
}) {
  const t = useT()
  const [open, setOpen] = useState(false)
  const lc = type?.lifecycle
  const current = lc ? values[lc.property] : undefined
  if (!lc && !appPage) return null
  const moves = (lc?.transitions ?? []).filter((tr) => tr.to !== current)
  const label = (state: string) => type?.fields.find((f) => f.id === lc?.property)?.options.find((o) => o.value === state)?.label ?? optionLabel(state)
  const primary = moves.filter((m) => typeof current === "string" && m.from.includes(current)).slice(0, 2)
  const rest = moves.filter((m) => !primary.includes(m))
  const go = (child: ReactNode, enabled: boolean, reason: string, key: string) =>
    enabled && appPage ? (
      <Button key={key} size="sm" variant="outline" asChild>
        <Link to="/$orgId/$environmentId/apps/$appId/$pageId" params={{ ...scope, appId: appPage.appId, pageId: appPage.pageId }} search={{ [appPage.param]: objectKey }}>
          {child}
        </Link>
      </Button>
    ) : (
      <Tooltip key={key} content={reason}>
        <span tabIndex={0} className="rounded-full outline-none focus-visible:ring-3 focus-visible:ring-ring/30">
          <Button size="sm" variant="outline" disabled>
            {child}
          </Button>
        </span>
      </Tooltip>
    )
  return (
    <div className="flex flex-wrap items-center gap-2">
      {primary.map((m) => go(t("ws.record.moveTo", { state: label(m.to) }), true, appPage ? "" : t("ws.record.noApp"), m.id))}
      {rest.length ? (
        <div className="relative">
          <Button size="sm" variant="ghost" aria-expanded={open} onClick={() => setOpen((v) => !v)}>
            {t("ws.record.moreActions")} <ChevronDown className={cn("transition-transform", open && "rotate-180")} />
          </Button>
          {open ? (
            <div className="absolute end-0 top-10 z-20 w-64 space-y-1 rounded-2xl border border-border bg-popover p-2 shadow-lg">
              {rest.map((m) => (
                <div key={m.id} className="flex items-center justify-between gap-2 rounded-lg px-2 py-1.5 text-sm text-muted-foreground">
                  <span>{t("ws.record.moveTo", { state: label(m.to) })}</span>
                  <span className="text-2xs text-subtle-foreground">{t("ws.record.availableFrom", { states: m.from.map(label).join(", ") })}</span>
                </div>
              ))}
            </div>
          ) : null}
        </div>
      ) : null}
      {appPage ? (
        <Button size="sm" variant="ghost" asChild>
          <Link to="/$orgId/$environmentId/apps/$appId/$pageId" params={{ ...scope, appId: appPage.appId, pageId: appPage.pageId }} search={{ [appPage.param]: objectKey }}>
            {t("ws.record.openInApp")} <SquareArrowOutUpRight className="rtl-mirror" />
          </Link>
        </Button>
      ) : null}
    </div>
  )
}

/** The handful of facts a reader looks for first: amount, date, quantity and the records this one belongs to. */
function referenceTargets(type: TypeMeta | undefined, values: Record<string, unknown>, masked: Masked) {
  if (!type) return []
  return type.fields
    .filter((f) => f.role === "reference" && !f.extension && typeof values[f.id] === "string" && !isMasked(masked, f.id))
    .flatMap((f) => {
      const link = type.links.find((l) => l.id === f.link)
      return link ? [{ field: f, objectType: link.target, key: values[f.id] as string }] : []
    })
}

function KeyFacts({
  scope,
  type,
  values,
  masked,
  refTargets,
  refs,
}: {
  scope: Scope
  type: TypeMeta
  values: Record<string, unknown>
  masked: Masked
  refTargets: ReturnType<typeof referenceTargets>
  refs: ReturnType<typeof useReferenceIndex>
}) {
  const t = useT()
  const { ontology } = useOntology(scope)
  const titleOf = useTitleOf(ontology)
  const facts = [type.amountField, type.dateField, type.fields.find((f) => f.role === "quantity" && !f.extension)?.id]
    .flatMap((id) => (id ? [type.fields.find((f) => f.id === id)] : []))
    .filter((f): f is FieldMeta => Boolean(f))
  if (!facts.length && !refTargets.length) return null
  const failed = refTargets.map((r) => refs(r.objectType, r.key)).filter((r) => r.isError).map((r) => r.error)
  return (
    <>
    <dl className="grid grid-cols-2 gap-x-8 gap-y-5 border-y border-border py-5 sm:grid-cols-3 lg:grid-cols-5">
      {facts.map((f) => (
        <div key={f.id} className="min-w-0 space-y-1">
          <dt className="text-xs text-muted-foreground">{f.label}</dt>
          <dd className={cn("truncate", f.role === "money" ? "text-xl font-semibold tracking-tight" : "text-[15px]")}>
            <FieldValue field={f} values={values} masked={masked} type={type} />
          </dd>
        </div>
      ))}
      {refTargets.map((r) => {
        const result = refs(r.objectType, r.key)
        const title = titleOf(result?.data, r.key)
        return (
          <div key={r.field.id} className="min-w-0 space-y-1">
            <dt className="text-xs text-muted-foreground">{r.field.label}</dt>
            <dd className="truncate text-[15px]">
              <Link
                to="/$orgId/$environmentId/records/$objectType/$key"
                params={{ ...scope, objectType: r.objectType, key: r.key }}
                search={{ via: "link" }}
                className="underline decoration-border underline-offset-4 transition-colors hover:decoration-foreground"
              >
                {result?.isPending ? <Skeleton className="inline-block h-3.5 w-24" /> : title.isKey ? <span dir="ltr">{r.key}</span> : title.title}
              </Link>
            </dd>
          </div>
        )
      })}
    </dl>
    <RefusalSummary errors={failed} title={t("ws.related.unreadable")} />
    </>
  )
}

/** Fields grouped for reading: details first, identifiers and bookkeeping folded away. */
function Details({ type, values, masked }: { type: TypeMeta; values: Record<string, unknown>; masked: Masked }) {
  const t = useT()
  const [systemOpen, setSystemOpen] = useState(false)
  const shownElsewhere = new Set([type.amountField, type.dateField, type.statusField, ...type.titleFields])
  // A field whose type admits a single value states a constant, not this record's state: fold it away.
  const constant = (f: FieldMeta) => f.role === "text" && f.options.length === 1
  const details = type.fields.filter((f) => !shownElsewhere.has(f.id) && !constant(f) && ["text", "contact", "flag", "money", "date", "quantity"].includes(f.role) && !f.extension && f.id in values)
  const hiddenTitles = type.fields.filter((f) => type.titleFields.includes(f.id) && isMasked(masked, f.id))
  const system = type.fields.filter((f) => (["identifier", "technical", "currency", "reference"].includes(f.role) || f.extension || constant(f)) && f.id in values)
  return (
    <section aria-labelledby="ws-details" className="space-y-3">
      <h2 id="ws-details" className="text-[15px] font-semibold tracking-tight">
        {t("ws.record.details")}
      </h2>
      <dl className="grid gap-x-10 sm:grid-cols-2">
        {[...hiddenTitles, ...details].map((f) => (
          <div key={f.id} className="grid grid-cols-[minmax(7rem,40%)_1fr] items-baseline gap-3 border-b border-border py-2.5">
            <dt className="truncate text-[13px] text-muted-foreground">{f.label}</dt>
            <dd className="min-w-0 text-sm [overflow-wrap:anywhere]">
              <FieldValue field={f} values={values} masked={masked} type={type} />
            </dd>
          </div>
        ))}
      </dl>
      {system.length ? (
        <div>
          <button type="button" aria-expanded={systemOpen} onClick={() => setSystemOpen((v) => !v)} className="flex items-center gap-1.5 py-1 text-xs text-muted-foreground outline-none hover:text-foreground focus-visible:text-foreground">
            <ChevronDown className={cn("size-3.5 transition-transform", !systemOpen && "-rotate-90 rtl:rotate-90")} aria-hidden />
            {t("ws.record.identifiers", { count: system.length })}
          </button>
          {systemOpen ? (
            <dl className="mt-1 grid gap-x-10 sm:grid-cols-2">
              {system.map((f) => (
                <div key={f.id} className="grid grid-cols-[minmax(7rem,40%)_1fr] items-baseline gap-3 border-b border-border/60 py-2">
                  <dt className="truncate text-xs text-muted-foreground">{f.label}</dt>
                  <dd className="min-w-0 text-xs [overflow-wrap:anywhere]">
                    <FieldValue field={f} values={values} masked={masked} type={type} />
                  </dd>
                </div>
              ))}
            </dl>
          ) : null}
        </div>
      ) : null}
    </section>
  )
}
